import styles from './budgetPDFPage.module.css'
import { Budget, Customer } from "../../../state/types"
import { useEffect, useMemo, useState } from 'react'
import { DocumentKey, useEmployeeLoaders } from '../../../../administration'
import { Product, useProductHooks, useProductLoaders } from '../../../../products'
import { useDataTypesHooks } from '../../../../../features/data-types'
import { Item, PriceBreakdown, usePriceOutlineHooks } from '../../../../operations'
import { PDFBranchesFooter, PDFHeader } from '../../../../../features/ui'
import { BudgetItemPDFRow } from './types'
import { firstItem } from '../../../../../utils/listUtils'
import { useCustomerLoaders } from '../../../loaders/customerLoaders'
import { TableName } from '../../../../../tables'
import { useBudgetHooks } from '../../../hooks/budgetHooks'
import { useSalesHooks } from '../../../hooks/salesHooks'

type Props = {
    pageIndex: number
    budget: Budget
    items: Item[]
    pagesAmount: number
}

export const BudgetPDFPage = (props: Props) => {
    const { pageIndex, budget, items, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1
    
    const { getBudgetPricesList } = useBudgetHooks()
    const { formatPrice, formatDate, formatIdentifier, formatPhone, formatPercentage } = useDataTypesHooks()
    const { describeMeasure, describeQuantity } = useProductHooks()
    const { getPriceOutline } = usePriceOutlineHooks()
    const { getDocumentFooter, getDocumentPaymentTypeIdsData } = useSalesHooks()

    const employeeLoaders = useEmployeeLoaders()
    const customerLoaders = useCustomerLoaders()
    const productLoaders = useProductLoaders()

    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [products, setProducts] = useState<Product[]>([])
    const [customer, setCustomer] = useState<Customer>()

    const init = async () => {
        const productIds = items.map(item => item.productId)
        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(productIds)
        setProducts(loadedProducts)
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(budget.customerId)
        setCustomer(loadedCustomer)   
        setAsyncDataLoaded(true)
    }

    useEffect(() => {
        init()
    }, [budget, items])

    const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.BUDGET), [getDocumentFooter])
    const documentPaymentTypeIdsData = useMemo(() => getDocumentPaymentTypeIdsData(DocumentKey.BUDGET), [getDocumentPaymentTypeIdsData])
    const seller = useMemo(() => employeeLoaders().findEmployee(budget.sellerId), [budget.sellerId])
    const priceOutline = useMemo(() => getPriceOutline(TableName.BUDGETS, budget), [budget])
    const priceItems = useMemo(() => {
        const paymentTypeIds = documentPaymentTypeIdsData?.[budget.customerTypeId]?.paymentTypeIds || []
        return getBudgetPricesList(priceOutline.total).filter(priceItem => paymentTypeIds.includes(priceItem.paymentType.id))
    }, [priceOutline.total, budget.customerTypeId])

    const columns = [
        { name: 'product', label: 'Producto' },
        { name: 'measure', label: 'Medida' },
        { name: 'unitTotal', label: 'P. Unitario' },
        { name: 'quantity', label: 'Cantidad' },
        { name: 'total', label: 'P. Total' }
    ]

    const rows: BudgetItemPDFRow[] = useMemo(() => asyncDataLoaded && products.length > 0 ?
        items.map(item => {
            const product = products.find(product => product.id === item.productId)!
            const itemPriceOutline = priceOutline.items.find(itemPriceOutline => itemPriceOutline.productId === product.id)!
            return {
                product: product.name,
                measure: describeMeasure(product),
                unitTotal: formatPrice(itemPriceOutline.unitTotal),
                quantity: describeQuantity(item.quantity, product),
                total: formatPrice(itemPriceOutline.total)
            }
        }) : []
    , [items, products, priceOutline, asyncDataLoaded])

    return (
        <div className={styles.container}>
            <div className={styles.body}>                
                <PDFHeader noValidTicketLabel />

                {pageIndex === 0 && (<>
                    <div className={styles.row}>
                        <span className={styles.budgetTitle}>
                            Presupuesto #{budget.code}
                        </span>
                        <div className={styles.date}>
                            Fecha: {formatDate(new Date(budget.date))}
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.personalInfo}>
                            <span>Cliente: {customer?.name}</span>
                            <span>Dirección: {customer?.mainAddress?.address || '-'}</span>
                            <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                            <span>Teléfono: {customer && customer.phones.length > 0 ? formatPhone(firstItem(customer!.phones)) : '-'}</span>
                        </div>
                        <div className={styles.personalInfo}>
                            <span>Vendedor: {seller?.name}</span>
                            <span>Teléfono: {seller?.phone ? formatPhone(seller.phone) : '-'}</span>
                        </div>
                    </div>
                </>)}

                <div className={styles.tableRow}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column, index) => (
                                    <th key={index}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        {columns.map((column, index) => (
                                            <td key={index}>
                                                {row[column.name as keyof BudgetItemPDFRow]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                <div className={styles.pagination}>
                    {pageIndex + 1} / {pagesAmount}
                </div>

                {isLastPage && priceOutline && (
                    <div className={`${styles.row} ${styles.priceOutline}`}>
                        <PriceBreakdown data={priceOutline} showDetail={false} />

                        {priceItems.map(priceItem => (
                            <div key={priceItem.paymentType.id} className={styles.totalLine}>
                                Precio {priceItem.paymentType.name} {!priceItem.paymentType.name.includes('%') ? ` (${formatPercentage(priceItem.paymentType.yield)})` : ''}: <strong>{formatPrice(priceItem.price)}</strong>
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.footer}>
                    <div className={styles.separator} />
                    <div className={`${styles.row} ${styles.footerRow}`}>
                        <span>
                            {documentFooter}
                        </span>
                        <PDFBranchesFooter />
                    </div>
                </div>
            </div>
        </div>
    )
}
